
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";

const resetPasswordSchema = z.object({
  password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
  confirmPassword: z.string().min(6, "A senha deve ter pelo menos 6 caracteres")
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"]
});

const ResetPassword = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { getPath } = useLanguagePath();
  
  const form = useForm<z.infer<typeof resetPasswordSchema>>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: ""
    }
  });
  
  const onSubmit = async (values: z.infer<typeof resetPasswordSchema>) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: values.password
      });
      
      if (error) {
        console.error("Erro ao redefinir senha:", error);
        toast.error(t('auth.resetPassword.errorMessage'));
        return;
      }
      
      toast.success(t('auth.resetPassword.successMessage'));
      
      // Redirect to login page
      navigate(getPath('/auth'));
    } catch (error: any) {
      console.error("Erro inesperado ao redefinir senha:", error);
      toast.error(t('auth.resetPassword.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-secondary/20 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8 bg-white dark:bg-gray-800/90 p-8 rounded-lg shadow-lg dark:border dark:border-gray-700/50">
        <div className="flex flex-col items-center space-y-4">
          <img src="/lovable-uploads/e79feb48-6e7c-488a-8626-bb79f4bde67a.png" alt="Logo" className="h-20 w-auto" />
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight dark:text-white">
              {t('auth.resetPassword.title')}
            </h1>
            <p className="text-sm text-muted-foreground dark:text-gray-400">
              {t('auth.resetPassword.subtitle')}
            </p>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="dark:text-gray-300">{t('auth.resetPassword.passwordLabel')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        {...field}
                        type="password"
                        className="pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder={t('auth.resetPassword.passwordPlaceholder')}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="dark:text-gray-300">{t('auth.resetPassword.confirmPasswordLabel')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        {...field}
                        type="password"
                        className="pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submitButton')}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default ResetPassword;
